import {
  ArrowForward,
  CheckCircleOutlineRounded,
  CloseRounded,
  SearchRounded,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Radio,
  SwipeableDrawer,
  TextField,
  Tooltip,
} from '@mui/material';

import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import SignIcon from '../../assets/IconSignature.svg';
import IcpIcon from '../../assets/IconCertificate.svg';
import {
  CardIcp,
  CardSign,
  Container,
  ContainerSuccess,
  Header,
  SelectedAllBtn,
  Text,
  TextBtn,
  Users,
} from './styles';
import { stringAvatar } from '../../utils/avatarGenerator';
import useCompanyStore from '../../store/companies';
import {
  Guest,
  getFileByIdRequest,
  getFoldersByFolderIdRequest,
  getUsersByCompanyIdRequest,
} from '../../services';
import { D4signDocument } from '../../services/requests/d4sign/types';
import useAuthStore from '../../store/auth';
import { sendDocumentForD4signRequest } from '../../services/requests/d4sign/utils';
import fileApi from '../../services/requests/arquivos/fileApi';

interface DrawerRequestSignaturesDocProps {
  setOpenDrawerSignatures: any;
  fileToSign: any;
  setFileId: (id: string) => void;
  onLoad: any;
}

interface GuestToSelect extends Guest {
  selected: boolean;
}

export const DrawerRequestSignaturesDoc = ({
  setOpenDrawerSignatures,
  fileToSign,
  setFileId,
  onLoad,
}: DrawerRequestSignaturesDocProps) => {
  const selectedCompany = useCompanyStore(state => state.selectedCompany);
  const user = useAuthStore(state => state.user);

  const [stepDrawer, setStepDrawer] = useState('signType');
  const [loadingMinute, setLoadingMinute] = useState(false);
  const [hasIcpSign, setHasIcpSign] = useState<boolean>(false);
  const [usersSelectedToSign, setUsersSelectedToSign] = useState<
    GuestToSelect[]
  >([]);
  const [listUsers, setListUsers] = useState<GuestToSelect[]>([]);
  const [listUsersFinal, setListUsersFinal] = useState<GuestToSelect[]>([]);
  const [searchForUsers, setSearchForUsers] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [documentBase64, setDocumentBase64] = useState<string>('');
  const [loadingInfos, setLoadingInfos] = useState(true);

  const loadUsers = useCallback(async () => {
    try {
      setLoadingInfos(true);
      const response = await getUsersByCompanyIdRequest(
        selectedCompany?.id as string,
        searchForUsers,
        '',
        '100',
        '',
      );

      setListUsers(response?.data?.data);
    } catch (err) {
      console.log(err);
    }
  }, [searchForUsers, selectedCompany?.id]);

  const loadFileInfo = useCallback(
    async (fileId: string) => {
      try {
        setLoadingInfos(true);
        const response = await getFileByIdRequest(
          fileId,
          selectedCompany?.id as string,
          user?.id as string,
        );

        const responseBlob = await fileApi.getBlob(
          response?.data.key,
          user?.id as string,
          selectedCompany?.id as string,
        );

        if (response?.status === 200) {
          let reader = new FileReader();
          reader.readAsDataURL(responseBlob);
          reader.onload = ev => {
            setDocumentBase64(ev.target?.result?.slice(28) as string);
          };
        }
      } catch (err) {
        console.log(err);
      } finally {
        console.log('finalizou');
      }
    },
    [selectedCompany?.id, user?.id],
  );

  const loadInfos = async () => {
    await Promise.all([loadUsers(), loadFileInfo(fileToSign.id)]).finally(
      () => {
        setLoadingInfos(false);
      },
    );
  };

  useEffect(() => {
    if (fileToSign.id) {
      loadInfos();
    }
  }, [fileToSign]);

  useEffect(() => {
    if (stepDrawer === 'request') {
      const filterUser = listUsers?.filter(users =>
        users.name.toUpperCase().includes(searchForUsers.toUpperCase()),
      );
      setListUsersFinal(filterUser?.length > 0 ? filterUser : listUsersFinal);
    }
  }, [searchForUsers, stepDrawer]);

  const handleUserToInviteFolder = useCallback(
    (usr: GuestToSelect, idx: number, type: string) => {
      let listaUsuariosEditada = { ...usr };

      if (type === 'select') {
        listaUsuariosEditada.selected = !usr?.selected;
      }

      if (searchForUsers?.length > 0) {
        const listFilteredWhenSearching = listUsers?.map(u => {
          if (u?.id === usr?.id) {
            setSearchForUsers('');
            return listaUsuariosEditada;
          }
          return u;
        });

        const usuariosMembros = listFilteredWhenSearching?.filter(
          u => u.selected,
        );

        setUsersSelectedToSign([...usuariosMembros!]);
        setListUsers(listFilteredWhenSearching);
        setListUsersFinal(listFilteredWhenSearching);
      } else {
        listUsers?.splice(idx, 1, listaUsuariosEditada);
        listUsersFinal?.splice(idx, 1, listaUsuariosEditada);
        setListUsers([...listUsers!]);
        setListUsersFinal([...listUsersFinal!]);
        const usuariosMembros = listUsers?.filter(u => u.selected);
        setUsersSelectedToSign([...usuariosMembros!]);
      }
    },
    [searchForUsers?.length, listUsers, listUsersFinal],
  );

  const handleConfigureAta = useCallback(async () => {
    try {
      setLoadingMinute(true);
      const signers = usersSelectedToSign?.map(u => ({
        id: u.id,
        name: u.name,
        email: u.email,
      }));

      const createDocumentForD4Sign: D4signDocument = {
        pdfBase64: documentBase64,
        fileName: fileToSign.original_name,
        ownerId: user?.id as string,
        signers,
        fileId: fileToSign.id,
        mimeType: 'application/pdf',
        isIcp: hasIcpSign,
      };

      const response = await sendDocumentForD4signRequest(
        createDocumentForD4Sign,
      );

      if (response?.status === 201) {
        // setUrlDocuSign(response?.data.url);
        setStepDrawer('success');
        // setOpenDrawerConfigureSignature(true);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingMinute(false);
    }
  }, [
    usersSelectedToSign,
    documentBase64,
    fileToSign.original_name,
    fileToSign.id,
    user?.id,
    hasIcpSign,
  ]);

  const handleIcpDrawer = () => {
    setStepDrawer('request');
  };

  const handleCloseDrawer = () => {
    setOpenDrawerSignatures(false);
    setStepDrawer('signType');
    setFileId(fileToSign.id);
  };

  const handleCloseDrawerSuccess = () => {
    handleCloseDrawer();
    onLoad();
  };

  return (
    <Box>
      {stepDrawer === 'signType' && (
        <Box width="480px" padding="20px">
          <Header>
            <CloseRounded
              sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
              onClick={handleCloseDrawer}
            />
            <span className="title">Assinatura</span>
          </Header>
          {loadingInfos ? (
            <Box display="flex" justifyContent="center" width="100%" mt="50px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Container marginTop="20px" overflow="auto">
                <CardSign>
                  <Radio
                    checked={hasIcpSign === false}
                    onChange={() => setHasIcpSign(prevState => !prevState)}
                    value={hasIcpSign}
                    name="radio-buttons"
                  />
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                  >
                    <img src={SignIcon} alt="Logo assinatura" />
                    <span className="textSign"> Assinatura Eletrônica </span>
                  </Box>
                </CardSign>
                <CardIcp>
                  <Radio
                    checked={hasIcpSign === true}
                    onChange={() => setHasIcpSign(prevState => !prevState)}
                    value={hasIcpSign}
                    name="radio-buttons"
                  />
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    flex="1 0 0"
                  >
                    <img src={IcpIcon} alt="Logo certificado" />
                    <span className="textSign"> Assinatura Digital </span>
                    <span className="textIcp">
                      Importante: os assinantes precisam ter o certificado
                      digital e-CPF (tipo A1 ou A3) da ICP-Brasil
                    </span>
                  </Box>
                </CardIcp>
              </Container>
            </>
          )}

          <Fragment key="right">
            <Button
              variant="contained"
              color="success"
              onClick={handleIcpDrawer}
              sx={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                color: '#FFFFFF',
                fontSize: '15px',
                padding: '6px 16px 6px 22px',
                display: 'flex',
                gap: '10px',
                fontWeight: '600',
              }}
            >
              Prosseguir <ArrowForward />
            </Button>

            {/* <SwipeableDrawer
              onClose={() => setOpenDrawerConfigureSignature(true)}
              onOpen={() => setOpenDrawerConfigureSignature(true)}
              open={openDrawerConfigureSignature}
              anchor="right"
            >
              {drawerConfigureSignature()}
            </SwipeableDrawer> */}
          </Fragment>
        </Box>
      )}
      {stepDrawer === 'request' && (
        <Box width="480px" padding="20px">
          <Header>
            <CloseRounded
              sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
              onClick={handleCloseDrawer}
            />
            <span className="title">Assinatura</span>
          </Header>
          {loadingMinute ? (
            <Box display="flex" justifyContent="center" width="100%" mt="50px">
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Container marginTop="20px" overflow="auto">
                <Text>Selecione quem irá assinar o documento</Text>
                {listUsers?.length > 0 ? (
                  <>
                    <TextField
                      variant="filled"
                      placeholder="Filtrar usuário..."
                      value={searchForUsers}
                      fullWidth
                      onChange={ev => setSearchForUsers(ev.target.value)}
                      InputProps={{
                        endAdornment: <SearchRounded />,
                      }}
                      sx={{ marginTop: '10px' }}
                    />
                    {loadingUsers ? (
                      <Box
                        width="100%"
                        display="flex"
                        justifyContent="center"
                        mt="20px"
                      >
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Box flex="1 0 0" overflow="auto" marginBottom="20px">
                          {listUsersFinal?.map((u: any, index: number) => (
                            <Users
                              onClick={() =>
                                handleUserToInviteFolder(
                                  u as GuestToSelect,
                                  index,
                                  'select',
                                )
                              }
                              key={u?.id}
                            >
                              <Checkbox
                                color="primary"
                                checked={u?.selected ?? false}
                                sx={{ padding: '0' }}
                              />
                              <Box
                                display="flex"
                                flexDirection="row"
                                gap="10px"
                              >
                                {u?.avatar_url ? (
                                  <Avatar sx={{ width: '40px' }}>
                                    <img
                                      src={u?.avatar_url as string}
                                      alt="Foto de perfil"
                                      style={{ width: '100%' }}
                                    />
                                  </Avatar>
                                ) : (
                                  <Avatar
                                    {...stringAvatar(u.name!)}
                                    sx={{ width: '40px' }}
                                  />
                                )}
                              </Box>
                              <span className="name">{u.name}</span>
                            </Users>
                          ))}
                        </Box>
                      </>
                    )}
                  </>
                ) : (
                  <Box marginTop="10px" flex="1 0 0">
                    Não há nenhum usuário convidado para adicionar
                  </Box>
                )}
              </Container>
            </>
          )}

          <Fragment key="right">
            <Button
              variant="contained"
              color="success"
              disabled={loadingMinute}
              onClick={handleConfigureAta}
              sx={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                color: '#FFFFFF',
                fontSize: '15px',
                padding: '6px 16px 6px 22px',
                display: 'flex',
                gap: '10px',
                fontWeight: '600',
              }}
            >
              Prosseguir <ArrowForward />
            </Button>

            {/* <SwipeableDrawer
              onClose={() => setOpenDrawerConfigureSignature(true)}
              onOpen={() => setOpenDrawerConfigureSignature(true)}
              open={openDrawerConfigureSignature}
              anchor="right"
            >
              {drawerConfigureSignature()}
            </SwipeableDrawer> */}
          </Fragment>
        </Box>
      )}
      {stepDrawer === 'success' && (
        <Box width="480px" padding="20px">
          <Header>
            <CloseRounded
              sx={{ cursor: 'pointer', position: 'absolute', left: '0' }}
              onClick={handleCloseDrawerSuccess}
            />
            <span className="title">Assinatura</span>
          </Header>
          <Box
            display="flex"
            alignItems="center"
            flexDirection="column"
            gap="10px"
            mt="50px"
          >
            <CheckCircleOutlineRounded
              color="success"
              sx={{ width: '66px', height: '66px' }}
            />
            <ContainerSuccess display="flex" flexDirection="column">
              <span className="title">Assinatura solicitada com sucesso!</span>
              <span className="desc">
                Os usuários serão solicitados para assinar o documento, e
                receberão um e-mail de notificação.
              </span>
            </ContainerSuccess>
            <Button
              variant="outlined"
              sx={{ marginTop: '20px' }}
              onClick={handleCloseDrawerSuccess}
            >
              Ok, fechar
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};
